/**
 * StudentCheckoutModal — Checkout directo del plan Estudiante con Transbank (v10.6.3)
 * Sin cuenta previa. Usuario elige ciclo, ingresa datos y va a WebPay.
 */
import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, CreditCard, Loader2, Check, ShieldCheck } from 'lucide-react';
import { toast } from 'sonner';

const formatCLP = (n) =>
  n ? new Intl.NumberFormat('es-CL', { style: 'currency', currency: 'CLP', maximumFractionDigits: 0 }).format(n) : '$0';

// Backend URL
const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CYCLES = [
  {
    id: 'mensual',
    label: 'Mensual',
    monto: 39900,
    sims: 25,
    equiv: '$39.900/mes',
    save: null,
  },
  {
    id: 'trimestral',
    label: '3 meses',
    monto: 99900,
    sims: 80,
    equiv: '$33.300/mes',
    save: 'Ahorras $19.800',
  },
  {
    id: 'semestral',
    label: '6 meses',
    monto: 179900,
    sims: 150,
    equiv: '$29.983/mes',
    save: 'Ahorras $59.500',
    highlight: true,
  },
];

export default function StudentCheckoutModal({ open, onClose }) {
  const [cycle, setCycle] = useState('mensual');
  const [buyerEmail, setBuyerEmail] = useState('');
  const [buyerName, setBuyerName] = useState('');
  const [buyerRut, setBuyerRut] = useState('');
  const [aceptaTerminos, setAceptaTerminos] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const selected = CYCLES.find((c) => c.id === cycle) || CYCLES[0];

  const handlePay = async () => {
    if (!buyerEmail.trim() || !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(buyerEmail)) {
      toast.error('Ingresa un correo válido');
      return;
    }
    if (!buyerName.trim()) {
      toast.error('Ingresa tu nombre');
      return;
    }
    if (!aceptaTerminos) {
      toast.error('Debes aceptar los términos y condiciones');
      return;
    }
    setSubmitting(true);
    try {
      const res = await fetch(`${API_URL}/transbank/create`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          monto_clp: selected.monto,
          descripcion: `Plan Estudiante — ${selected.label} (${selected.sims} simulaciones)`,
          plan_codigo: 'estudiante',
          tipo: 'student_subscription',
          buyer_email: buyerEmail.trim().toLowerCase(),
          buyer_name: buyerName.trim(),
          buyer_rut: buyerRut.trim(),
          cycle,
        }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || 'Error al iniciar el pago');
      }
      const data = await res.json();
      // En integración, WebPay real espera un POST del token para redirigir.
      // Como usamos simulación, redirigimos directamente al return con status paid.
      if (data.environment === 'integration') {
        // Confirmamos automáticamente el pago simulado
        window.location.href = `${API_URL}/transbank/return?token_ws=${data.token}`;
      } else {
        // Producción: redirigir a WebPay real
        // WebPay requiere un formulario POST — hacemos submit programático
        const form = document.createElement('form');
        form.method = 'POST';
        form.action = data.redirect_url;
        const input = document.createElement('input');
        input.type = 'hidden';
        input.name = 'token_ws';
        input.value = data.token;
        form.appendChild(input);
        document.body.appendChild(form);
        form.submit();
      }
    } catch (e) {
      toast.error(e.message || 'Error al procesar el pago');
      setSubmitting(false);
    }
  };

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-[100] bg-black/70 backdrop-blur-sm flex items-center justify-center p-4"
          onClick={onClose}
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.95, y: 12 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95 }}
            transition={{ duration: 0.2 }}
            className="bg-slate-900 border border-slate-700 rounded-2xl w-full max-w-2xl max-h-[92vh] overflow-y-auto shadow-2xl"
            onClick={(e) => e.stopPropagation()}
            data-testid="student-checkout-modal"
          >
            {/* Header */}
            <div className="px-6 py-4 border-b border-slate-800 flex items-center justify-between sticky top-0 bg-slate-900 z-10">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-cyan-500/20 flex items-center justify-center">
                  <CreditCard size={20} className="text-cyan-400" />
                </div>
                <div>
                  <div className="text-white font-semibold">Plan Estudiante</div>
                  <div className="text-slate-400 text-xs">Activación inmediata al pagar</div>
                </div>
              </div>
              <button onClick={onClose} className="text-slate-400 hover:text-white" data-testid="close-checkout-btn">
                <X size={20} />
              </button>
            </div>

            {/* Selección de ciclo */}
            <div className="px-6 py-5">
              <label className="text-slate-300 text-xs uppercase tracking-widest font-medium">
                1. Elige tu ciclo
              </label>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mt-2">
                {CYCLES.map((c) => (
                  <button
                    key={c.id}
                    onClick={() => setCycle(c.id)}
                    data-testid={`cycle-${c.id}`}
                    className={`relative text-left p-4 rounded-xl border-2 transition-all ${
                      cycle === c.id
                        ? 'border-cyan-500 bg-cyan-500/10'
                        : 'border-slate-700 bg-slate-800/50 hover:border-slate-600'
                    }`}
                  >
                    {c.highlight && (
                      <span className="absolute -top-2 right-2 px-2 py-0.5 text-[10px] bg-gradient-to-r from-cyan-500 to-emerald-500 text-white rounded-full font-semibold">
                        Mejor valor
                      </span>
                    )}
                    <div className="text-white font-semibold text-sm">{c.label}</div>
                    <div className="text-2xl text-white font-bold mt-1">{formatCLP(c.monto)}</div>
                    <div className="text-cyan-400 text-xs mt-1">{c.sims} simulaciones</div>
                    <div className="text-slate-400 text-[11px]">{c.equiv}</div>
                    {c.save && (
                      <div className="text-emerald-400 text-[11px] mt-1 font-medium">{c.save}</div>
                    )}
                    {cycle === c.id && (
                      <div className="absolute top-2 right-2 w-5 h-5 rounded-full bg-cyan-500 flex items-center justify-center">
                        <Check size={12} className="text-white" />
                      </div>
                    )}
                  </button>
                ))}
              </div>
            </div>

            {/* Datos comprador */}
            <div className="px-6 pb-5">
              <label className="text-slate-300 text-xs uppercase tracking-widest font-medium">
                2. Datos de tu cuenta
              </label>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-2">
                <div className="sm:col-span-2">
                  <label className="text-slate-400 text-xs block mb-1">Correo electrónico *</label>
                  <input
                    type="email"
                    value={buyerEmail}
                    onChange={(e) => setBuyerEmail(e.target.value)}
                    placeholder="tu@correo.cl"
                    data-testid="checkout-email"
                    className="w-full px-3 py-2.5 bg-slate-800 border border-slate-700 rounded-lg text-white text-sm focus:outline-none focus:border-cyan-500"
                  />
                  <p className="text-[11px] text-slate-500 mt-1">
                    Aquí llegará el acceso a tu cuenta al confirmar el pago.
                  </p>
                </div>
                <div>
                  <label className="text-slate-400 text-xs block mb-1">Nombre y apellido *</label>
                  <input
                    type="text"
                    value={buyerName}
                    onChange={(e) => setBuyerName(e.target.value)}
                    placeholder="Ej: Ana Rodríguez"
                    data-testid="checkout-name"
                    className="w-full px-3 py-2.5 bg-slate-800 border border-slate-700 rounded-lg text-white text-sm focus:outline-none focus:border-cyan-500"
                  />
                </div>
                <div>
                  <label className="text-slate-400 text-xs block mb-1">RUT (opcional)</label>
                  <input
                    type="text"
                    value={buyerRut}
                    onChange={(e) => setBuyerRut(e.target.value)}
                    placeholder="11.111.111-1"
                    data-testid="checkout-rut"
                    className="w-full px-3 py-2.5 bg-slate-800 border border-slate-700 rounded-lg text-white text-sm focus:outline-none focus:border-cyan-500"
                  />
                </div>
              </div>
            </div>

            {/* Resumen + Términos */}
            <div className="px-6 pb-5">
              <div className="p-4 bg-slate-800/50 border border-slate-700 rounded-xl">
                <div className="flex items-center justify-between text-sm">
                  <span className="text-slate-400">{selected.label} · {selected.sims} simulaciones</span>
                  <span className="text-white font-semibold">{formatCLP(selected.monto)}</span>
                </div>
                <div className="flex items-center justify-between text-sm mt-2">
                  <span className="text-slate-400">IVA</span>
                  <span className="text-slate-500 text-xs">Incluido</span>
                </div>
                <div className="border-t border-slate-700 mt-3 pt-3 flex items-center justify-between">
                  <span className="text-white font-medium">Total a pagar hoy</span>
                  <span className="text-2xl text-cyan-400 font-bold">{formatCLP(selected.monto)}</span>
                </div>
              </div>

              <label className="flex items-start gap-2 mt-4 text-slate-400 text-xs cursor-pointer">
                <input
                  type="checkbox"
                  checked={aceptaTerminos}
                  onChange={(e) => setAceptaTerminos(e.target.checked)}
                  data-testid="accept-terms"
                  className="mt-0.5 accent-cyan-500"
                />
                <span>
                  Acepto los <a href="/terminos" target="_blank" className="text-cyan-400 hover:underline">términos y condiciones</a> y
                  la <a href="/privacidad" target="_blank" className="text-cyan-400 hover:underline">política de privacidad</a> de SimuClinic.
                  Entiendo que las simulaciones no consumidas vencen al finalizar el ciclo.
                </span>
              </label>
            </div>

            {/* Botón pagar */}
            <div className="px-6 pb-6">
              <button
                onClick={handlePay}
                disabled={submitting}
                data-testid="pay-now-btn"
                className="w-full py-3.5 rounded-xl font-semibold bg-gradient-to-r from-cyan-500 to-blue-600 text-white hover:opacity-90 transition-all flex items-center justify-center gap-2 disabled:opacity-60 disabled:cursor-not-allowed"
              >
                {submitting ? (
                  <>
                    <Loader2 size={18} className="animate-spin" />
                    Redirigiendo a WebPay…
                  </>
                ) : (
                  <>
                    <CreditCard size={18} />
                    Pagar {formatCLP(selected.monto)} con WebPay
                  </>
                )}
              </button>
              <div className="mt-3 flex items-center justify-center gap-2 text-slate-500 text-xs">
                <ShieldCheck size={14} className="text-emerald-500" />
                <span>Pago seguro procesado por Transbank WebPay Plus</span>
              </div>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
